/**
 * Transient notices. The old app kept `toast` state with a 3 s timer (original/src/App.tsx:2898-2908)
 * but never rendered it; this shell renders the same {message, type} shape with the card classes of the
 * old premium cards so saves and API errors become visible (ADR-06 §2.3 error envelope → text).
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, CheckCircle, Info } from "lucide-react";

export type ToastType = "success" | "error" | "info";

interface ToastState {
  readonly message: string;
  readonly type: ToastType;
}

const ToastContext = createContext<(message: string, type?: ToastType) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback((message: string, type: ToastType = "success") => {
    setToast({ message, type });
    if (timer.current) clearTimeout(timer.current);
    // [C28] errors stay longer: an unexpected one carries a trace reference the user may read out (D-259)
    timer.current = setTimeout(() => setToast(null), type === "error" ? 8000 : 3000);
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div className="fixed bottom-6 inset-x-0 z-[200] flex justify-center pointer-events-none" data-testid="toast" data-toast-type={toast.type}>
          <div
            className={`pointer-events-auto flex items-center gap-3 px-6 py-3 rounded-2xl shadow-2xl border text-sm font-black backdrop-blur-xl ${
              toast.type === "error"
                ? "bg-rose-50/95 dark:bg-rose-900/40 border-rose-200 dark:border-rose-800/50 text-rose-700 dark:text-rose-300"
                : toast.type === "info"
                  ? "bg-blue-50/95 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800/50 text-blue-700 dark:text-blue-300"
                  : "bg-emerald-50/95 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800/50 text-emerald-700 dark:text-emerald-300"
            }`}
          >
            {toast.type === "error" ? <AlertTriangle size={18} /> : toast.type === "info" ? <Info size={18} /> : <CheckCircle size={18} />}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
}
